import { useEffect, useState } from 'react';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import {
	useCanAdministerJira,
	useCanAdministerProject,
} from '@atlassian/jira-project-permissions-service/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Version, VersionId } from '../../common/model/index.tsx';
import {
	getUnresolvedIssuesCount,
	getVersion as getVersionRequest,
} from '../../common/rest/index.tsx';
import {
	transformUnresolvedIssuesCount,
	transformVersion,
} from '../../common/services/transformers.tsx';
import type { ReleasableVersion } from '../model/index.tsx';
import { releaseVersion as releaseVersionRest } from '../rest/index.tsx';
import { addDefaultReleaseDate, transformReleasableToRestVersion } from './transformers.tsx';
import {
	dismissReleaseAutomationBanner,
	fireReleaseAutomationBannerError,
	getHasDismissedReleaseAutomationBanner,
} from './utils.tsx';

export const getVersion = (versionId: VersionId): Promise<ReleasableVersion> =>
	getVersionRequest(versionId).then(transformVersion).then(addDefaultReleaseDate);

export const getUnresolvedIssues = (versionId: VersionId): Promise<number> =>
	getUnresolvedIssuesCount(versionId).then(transformUnresolvedIssuesCount);

export const releaseVersion = (version: ReleasableVersion): Promise<Version> =>
	releaseVersionRest(transformReleasableToRestVersion(version)).then(transformVersion);

export const useReleaseAutomationBanner = () => {
	const [showBanner, setShowBanner] = useState(false);
	const accountId = useAccountId();
	const { data: projectContext } = useProjectContext();
	const safeProjectKey = projectContext?.projectKey ?? '';
	const isJiraAdmin = useCanAdministerJira(safeProjectKey);
	const isProjectAdmin = useCanAdministerProject(safeProjectKey);

	useEffect(() => {
		if (accountId != null) {
			getHasDismissedReleaseAutomationBanner(accountId)
				.then((hasDismissed) => {
					const canSee = !fg('vmc-48-hide-automation-link-for-da') || isJiraAdmin || isProjectAdmin;
					setShowBanner(!hasDismissed && canSee);
				})
				.catch(fireReleaseAutomationBannerError);
		}
	}, [accountId, isJiraAdmin, isProjectAdmin]);

	const dismissBanner = async () => {
		setShowBanner(false);
		if (accountId != null) {
			await dismissReleaseAutomationBanner(accountId).catch(fireReleaseAutomationBannerError);
		}
	};

	return { showBanner, dismissBanner };
};
